import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Car, Loader2, Lock, LogOut, Plus, Pencil, Trash2, Save, X, Upload,
  Search, Wrench, Shield, FileText, Star, Users, Wallet,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = ['Car', 'Bike', 'Scooter', 'Van', 'Truck', 'Other'];
const FUEL_TYPES    = ['Petrol', 'Diesel', 'Electric', 'CNG', 'Hybrid', 'LPG', 'Other'];
const STATUSES = ['Active', 'Under Service', 'Retired', 'Sold'];

const STATUS_TONE = {
  'Active':        'bg-green-100 text-green-800 border-green-200',
  'Under Service': 'bg-amber-100 text-amber-800 border-amber-200',
  'Retired':       'bg-gray-100 text-gray-700 border-gray-200',
  'Sold':          'bg-red-50  text-red-700  border-red-200',
};

const money = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

// DD-MM-YYYY <-> YYYY-MM-DD helpers (native date input needs ISO)
const toISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
};
const fromISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};
const daysUntil = (ddmmyyyy) => {
  const iso = toISODate(ddmmyyyy);
  if (!iso) return null;
  const then = new Date(iso + 'T00:00:00');
  const now  = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((then - now) / (1000 * 60 * 60 * 24));
};

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const emptyVehicle = () => ({
  name: '',
  registration_number: '',
  make_model: '',
  fuel_type: 'Petrol',
  vehicle_type: 'Car',
  year_of_purchase: '',
  color: '',
  photo_path: null,
  rc_card_path: null,
  assigned_to_employee_id: '',
  status: 'Active',
  insurance_provider: '',
  insurance_policy_number: '',
  insurance_expiry_date: '',
  maintenance_rating: 0,
  maintenance_notes: '',
  service_history: [],
  loan_taken: false,
  loan_bank: '',
  loan_amount: '',
  loan_sanction_date: '',
  emi_date: '',
  emi_amount: '',
  interest_rate: '',
  loan_closure_date: '',
  lien_status: '',
  emi_schedule_path: null,
  total_loan_interest: '',
  date_of_purchase: '',
  purchase_hand: '',
  purchase_invoice_path: null,
  sort_order: 100,
});

const StarRow = ({ value, onChange, size = 18, readOnly = false }) => {
  const [hover, setHover] = useState(null);
  const active = hover ?? value;
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const half   = active >= n - 0.5 && active < n;
        const full   = active >= n;
        return (
          <span
            key={n}
            className={`relative inline-block ${readOnly ? '' : 'cursor-pointer'}`}
            style={{ width: size, height: size }}
            onMouseLeave={() => !readOnly && setHover(null)}
          >
            <Star size={size} className="text-gray-300" />
            {(half || full) && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: full ? '100%' : '50%' }}
              >
                <Star size={size} className="text-amber-500 fill-amber-500" />
              </span>
            )}
            {!readOnly && (
              <>
                <span
                  className="absolute inset-y-0 left-0 w-1/2 z-10"
                  onMouseEnter={() => setHover(n - 0.5)}
                  onClick={() => onChange(n - 0.5)}
                />
                <span
                  className="absolute inset-y-0 right-0 w-1/2 z-10"
                  onMouseEnter={() => setHover(n)}
                  onClick={() => onChange(n)}
                />
              </>
            )}
          </span>
        );
      })}
    </div>
  );
};

const FieldGroup = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2">
      {label}
    </label>
    {children}
  </div>
);

const VehicleDiaryPage = () => {
  const { user, ready, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [vehicles, setVehicles]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Filters
  const [q, setQ]                     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');

  // Editor
  const [editing, setEditing] = useState(null);
  const [zoomPhoto, setZoomPhoto] = useState(null);   // { url, name } for the lightbox
  const [saving, setSaving]   = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [rcUploading, setRcUploading]       = useState(false);
  const photoRef = useRef(null);
  const rcRef    = useRef(null);
  const emiRef   = useRef(null);
  const invoiceRef = useRef(null);
  const [emiUploading, setEmiUploading] = useState(false);
  const [invoiceUploading, setInvoiceUploading] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/vehicles', { replace: true });
  }, [ready, user, navigate]);

  // ESC closes the photo lightbox
  useEffect(() => {
    if (!zoomPhoto) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomPhoto(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomPhoto]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [{ data: vs }, { data: emps }] = await Promise.all([
        axios.get(`${API}/admin/vehicles`),
        axios.get(`${API}/admin/employees?limit=500`),
      ]);
      setVehicles(Array.isArray(vs) ? vs : []);
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (ready && user) load(); /* eslint-disable-next-line */ }, [ready, user]);

  const employeeMap = useMemo(() => {
    const m = new Map();
    for (const e of employees) m.set(e.id, e);
    return m;
  }, [employees]);

  const activeEmployees = useMemo(
    () => employees.filter((e) => !e.date_of_leaving).sort((a, b) => a.name.localeCompare(b.name)),
    [employees]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (statusFilter && v.status !== statusFilter) return false;
      if (typeFilter && v.vehicle_type !== typeFilter) return false;
      if (needle) {
        const assigneeName = employeeMap.get(v.assigned_to_employee_id)?.name || '';
        const hay = `${v.name} ${v.registration_number} ${v.make_model || ''} ${v.fuel_type || ''} ${assigneeName} ${v.insurance_provider || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [vehicles, q, statusFilter, typeFilter, employeeMap]);

  // KPIs
  const stats = useMemo(() => {
    const s = { total: vehicles.length, active: 0, under_service: 0, insurance_due: 0 };
    for (const v of vehicles) {
      if (v.status === 'Active') s.active++;
      if (v.status === 'Under Service') s.under_service++;
      const d = daysUntil(v.insurance_expiry_date);
      if (d !== null && d <= 30) s.insurance_due++;
    }
    return s;
  }, [vehicles]);

  const openNew  = () => { setEditing(emptyVehicle()); setError(''); };
  const openEdit = (v) => {
    setEditing({
      ...emptyVehicle(),
      ...v,
      service_history: Array.isArray(v.service_history) ? v.service_history : [],
      year_of_purchase: v.year_of_purchase ?? '',
    });
    setError('');
  };
  const cancel = () => { setEditing(null); setError(''); };
  const handleField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'photo');
      const { data } = await axios.post(`${API}/admin/vehicles/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, photo_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Photo upload failed');
    } finally { setPhotoUploading(false); }
  };

  const uploadRc = async (file) => {
    if (!file) return;
    setRcUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'rc_card');
      const { data } = await axios.post(`${API}/admin/vehicles/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, rc_card_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'RC upload failed');
    } finally { setRcUploading(false); }
  };

  const uploadEmiSchedule = async (file) => {
    if (!file) return;
    setEmiUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'emi_schedule');
      const { data } = await axios.post(`${API}/admin/vehicles/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({
        ...e,
        emi_schedule_path: data.path,
        total_loan_interest: (data.extracted_interest != null && (e.total_loan_interest === '' || e.total_loan_interest == null))
          ? data.extracted_interest : e.total_loan_interest,
      }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'EMI schedule upload failed');
    } finally { setEmiUploading(false); }
  };

  const uploadInvoice = async (file) => {
    if (!file) return;
    setInvoiceUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'purchase_invoice');
      const { data } = await axios.post(`${API}/admin/vehicles/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, purchase_invoice_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Invoice upload failed');
    } finally { setInvoiceUploading(false); }
  };

  const addService = () => {
    setEditing((e) => ({
      ...e,
      service_history: [
        ...(e.service_history || []),
        { date: '', service_center: '', description: '', cost: 0, odometer: '' },
      ],
    }));
  };
  const updateService = (idx, key, value) => {
    setEditing((e) => ({
      ...e,
      service_history: e.service_history.map((row, i) => i === idx ? { ...row, [key]: value } : row),
    }));
  };
  const removeService = (idx) => {
    setEditing((e) => ({
      ...e,
      service_history: e.service_history.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (!editing.name?.trim() || !editing.registration_number?.trim()) {
      setError('Vehicle Name and Registration Number are required'); return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        name: editing.name.trim(),
        registration_number: editing.registration_number.trim().toUpperCase(),
        make_model: editing.make_model || null,
        fuel_type: editing.fuel_type || null,
        vehicle_type: editing.vehicle_type || 'Car',
        year_of_purchase: editing.year_of_purchase ? Number(editing.year_of_purchase) : null,
        color: editing.color || null,
        photo_path: editing.photo_path || null,
        rc_card_path: editing.rc_card_path || null,
        assigned_to_employee_id: editing.assigned_to_employee_id || null,
        status: editing.status || 'Active',
        insurance_provider: editing.insurance_provider || null,
        insurance_policy_number: editing.insurance_policy_number || null,
        insurance_expiry_date: editing.insurance_expiry_date || null,
        maintenance_rating: Number(editing.maintenance_rating) || 0,
        maintenance_notes: editing.maintenance_notes || null,
        service_history: (editing.service_history || []).map((row) => ({
          date: row.date || null,
          service_center: row.service_center || null,
          description: row.description || null,
          cost: Number(row.cost) || 0,
          odometer: row.odometer ? Number(row.odometer) : null,
        })),
        sort_order: Number(editing.sort_order) || 100,
        loan_taken: !!editing.loan_taken,
        loan_bank: editing.loan_taken ? (editing.loan_bank || null) : null,
        loan_amount: editing.loan_taken && editing.loan_amount !== '' ? Number(editing.loan_amount) : null,
        loan_sanction_date: editing.loan_taken ? (editing.loan_sanction_date || null) : null,
        emi_date: editing.loan_taken ? (editing.emi_date || null) : null,
        emi_amount: editing.loan_taken && editing.emi_amount !== '' ? Number(editing.emi_amount) : null,
        interest_rate: editing.loan_taken && editing.interest_rate !== '' ? Number(editing.interest_rate) : null,
        loan_closure_date: editing.loan_taken ? (editing.loan_closure_date || null) : null,
        lien_status: editing.loan_taken ? (editing.lien_status || null) : null,
        emi_schedule_path: editing.loan_taken ? (editing.emi_schedule_path || null) : null,
        total_loan_interest: editing.loan_taken && editing.total_loan_interest !== '' ? Number(editing.total_loan_interest) : null,
        date_of_purchase: editing.date_of_purchase || null,
        purchase_hand: editing.purchase_hand || null,
        purchase_invoice_path: editing.purchase_invoice_path || null,
      };
      if (editing.id) await axios.put(`${API}/admin/vehicles/${editing.id}`, payload);
      else            await axios.post(`${API}/admin/vehicles`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.name} (${v.registration_number})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${v.id}`);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    }
  };

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="text-brand-yellow mx-auto mb-4" size={32} />
          <p className="text-white/70 text-sm">Verifying access…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="vehicle-diary-page">
      {/* Admin control bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-[7.6923%] py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <Lock size={14} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">
                Admin Dashboard
              </div>
              <div className="text-sm font-medium">Vehicle Diary · {vehicles.length} vehicles</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openNew}
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none h-9 px-4 text-sm font-semibold"
              data-testid="add-vehicle-btn"
            >
              <Plus size={16} className="mr-1.5" /> Add Vehicle
            </Button>
            <button
              onClick={() => navigate('/team')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Team Directory"
              data-testid="nav-team-btn"
            >
              <Users size={13} /> Team
            </button>
            <button
              onClick={() => navigate('/admin/cashflow')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Internal Cashflow register"
              data-testid="nav-cashflow-btn"
            >
              <Wallet size={13} /> Cashflow
            </button>
            <button
              onClick={() => navigate('/admin/payroll')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Payroll register"
              data-testid="nav-payroll-btn"
            >
              <span className="font-mono tracking-widest">₹</span> Payroll
            </button>
            <button
              onClick={() => navigate('/admin/insurances')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Insurance register"
              data-testid="nav-insurances-btn"
            >
              <span aria-hidden>🛡</span> Insurances
            </button>
            <button
              onClick={() => navigate('/admin/policies')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Policies & SOP"
              data-testid="nav-policies-btn"
            >
              <span aria-hidden>📘</span> Policies
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/firm-documents')}
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
                title="Confidential Firm Documents"
                data-testid="nav-firm-docs-btn"
              >
                <span aria-hidden>📁</span> Firm Docs
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/access')}
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
                title="User & Access Control"
                data-testid="nav-access-btn"
              >
                <span aria-hidden>🔐</span> Access
              </button>
            )}
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">
              {user.email}
            </span>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"
              data-testid="vehicles-logout-btn"
              title="Sign out of admin panel"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-[#0a0f1e] pt-14 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-brand-yellow/15 blur-[160px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-4">
                <Car size={14} className="text-brand-yellow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  Internal · R S T &amp; Co.
                </span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.02] tracking-tight">
                Office Vehicle Diary
              </h1>
              <p className="text-sm md:text-base text-white/60 mt-3 max-w-2xl">
                Track company vehicles &mdash; RC cards, assigned custodians, insurance renewals, and service history.
              </p>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            <KPI label="Total Vehicles" value={String(stats.total)} icon={<Car size={14} />} tone="yellow" />
            <KPI label="Active"         value={String(stats.active)} icon={<Shield size={14} />} tone="green" />
            <KPI label="Under Service"  value={String(stats.under_service)} icon={<Wrench size={14} />} tone="amber" />
            <KPI label="Insurance Due (≤30d)" value={String(stats.insurance_due)} icon={<Shield size={14} />} tone={stats.insurance_due > 0 ? 'red' : 'mono'} />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 py-4 sticky top-[60px] z-20">
        <div className="max-w-[1400px] mx-auto px-[7.6923%] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, reg number, assignee, insurer…"
              className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-gray-900"
              data-testid="vehicle-search"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-900 bg-white"
            data-testid="vehicle-type-filter"
          >
            <option value="">All Types</option>
            {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-900 bg-white"
            data-testid="vehicle-status-filter"
          >
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </section>

      {/* Table */}
      <section className="max-w-[1400px] mx-auto px-[7.6923%] py-8">
        {error && (
          <div className="border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-2 mb-4" data-testid="vehicle-error">
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-24 text-center text-gray-500">
            <Loader2 className="mx-auto mb-3 animate-spin" size={22} />
            Loading vehicles…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 py-24 text-center">
            <Car size={36} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm mb-4">
              {vehicles.length === 0 ? 'No vehicles yet.' : 'No vehicles match the current filters.'}
            </p>
            {vehicles.length === 0 && (
              <Button
                onClick={openNew}
                className="bg-gray-900 hover:bg-black text-white rounded-none"
                data-testid="add-first-vehicle-btn"
              >
                <Plus size={14} className="mr-1.5" /> Add first vehicle
              </Button>
            )}
          </div>
        ) : (
          <div className="border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-left">
                <tr className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">
                  <th className="px-3 py-3 w-[104px]">Photo</th>
                  <th className="px-3 py-3">Vehicle</th>
                  <th className="px-3 py-3 hidden md:table-cell">Reg No.</th>
                  <th className="px-3 py-3 hidden lg:table-cell">Assigned to</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 hidden lg:table-cell">Insurance</th>
                  <th className="px-3 py-3 hidden xl:table-cell">Rating</th>
                  <th className="px-3 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const assignee = employeeMap.get(v.assigned_to_employee_id);
                  const daysLeft = daysUntil(v.insurance_expiry_date);
                  const insTone =
                    daysLeft === null ? 'text-gray-400' :
                    daysLeft < 0 ? 'text-red-700 font-semibold' :
                    daysLeft <= 30 ? 'text-amber-700 font-semibold' :
                    'text-gray-700';
                  return (
                    <tr key={v.id} className="border-b border-gray-100 hover:bg-gray-50/70" data-testid={`vehicle-row-${v.id}`}>
                      <td className="px-3 py-2">
                        {v.photo_path ? (
                          <button
                            type="button"
                            onClick={() => setZoomPhoto({ url: `${API}/media/${v.photo_path}`, name: v.name })}
                            className="w-[88px] h-[88px] bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center cursor-zoom-in hover:ring-2 hover:ring-brand-yellow transition-all"
                            title="Click to zoom"
                            data-testid={`zoom-vehicle-photo-${v.id}`}
                          >
                            <img src={`${API}/media/${v.photo_path}`} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="w-[88px] h-[88px] bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center">
                            <Car size={32} className="text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{v.name}</div>
                        <div className="text-[11px] text-gray-500">
                          {v.make_model || v.vehicle_type}
                          {v.year_of_purchase ? ` · ${v.year_of_purchase}` : ''}
                          {v.color ? ` · ${v.color}` : ''}
                          {v.fuel_type ? ` · ${v.fuel_type}` : ''}
                        </div>
                        <div className="md:hidden text-[11px] font-mono text-gray-700 mt-0.5">{v.registration_number}</div>
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <span className="font-mono text-xs text-gray-800">{v.registration_number}</span>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        {assignee ? (
                          <div>
                            <div className="text-gray-900 text-sm">{assignee.name}</div>
                            <div className="text-[10px] text-gray-500">{assignee.designation}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block text-[11px] px-2 py-0.5 border ${STATUS_TONE[v.status] || STATUS_TONE.Active}`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        {v.insurance_expiry_date ? (
                          <div>
                            <div className={`text-xs ${insTone}`}>{v.insurance_expiry_date}</div>
                            <div className="text-[10px] text-gray-500">
                              {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` :
                               daysLeft === 0 ? 'Expires today' :
                               `${daysLeft}d left`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Not recorded</span>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden xl:table-cell">
                        <StarRow value={v.maintenance_rating || 0} readOnly size={14} onChange={() => {}} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => openEdit(v)}
                            className="p-1.5 border border-gray-200 hover:border-gray-900 hover:bg-gray-100 transition-colors"
                            title="Edit"
                            data-testid={`edit-vehicle-${v.id}`}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => remove(v)}
                            className="p-1.5 border border-red-100 text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                            data-testid={`delete-vehicle-${v.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex justify-end" onClick={cancel} data-testid="vehicle-editor">
          <div
            className="w-full bg-white h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[9px] tracking-widest text-brand-yellow uppercase">
                  {editing.id ? 'Edit vehicle' : 'Add vehicle'}
                </div>
                <div className="font-heading text-xl font-bold text-gray-900">
                  {editing.name || 'New Vehicle'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={save} disabled={saving} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="save-vehicle-btn">
                  {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save</>}
                </Button>
                <button onClick={cancel} className="p-2 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" title="Close" data-testid="close-vehicle-editor">
                  <X size={16} />
                </button>
              </div>
            </div>

            {error && (
              <div className="mx-6 mt-4 border border-red-300 bg-red-50 text-red-800 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <div className="p-6 space-y-5">
              {/* Photo & RC uploads */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Photo">
                  <div className="flex items-center gap-4">
                    {editing.photo_path ? (
                      <button
                        type="button"
                        onClick={() => setZoomPhoto({ url: `${API}/media/${editing.photo_path}`, name: editing.name || 'Vehicle photo' })}
                        className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden cursor-zoom-in hover:ring-2 hover:ring-brand-yellow transition-all"
                        title="Click to zoom"
                        data-testid="editor-zoom-photo"
                      >
                        <img src={`${API}/media/${editing.photo_path}`} alt="" className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <div className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                        <Car size={22} className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input ref={photoRef} type="file" accept="image/*" className="hidden"
                        onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                      <Button type="button" onClick={() => photoRef.current?.click()} disabled={photoUploading}
                        className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9"
                        data-testid="upload-vehicle-photo-btn">
                        {photoUploading
                          ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                          : <><Upload size={14} className="mr-2" /> Upload / Replace</>}
                      </Button>
                      <p className="text-[11px] text-gray-500 mt-1">JPEG / PNG · up to 10 MB.</p>
                    </div>
                  </div>
                </FieldGroup>

                <FieldGroup label="RC Card">
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden text-center px-2">
                      {editing.rc_card_path ? (
                        <a href={`${API}/media/${editing.rc_card_path}`} target="_blank" rel="noreferrer" className="text-xs text-brand-blue hover:underline break-all">
                          <FileText size={20} className="mx-auto mb-1 text-gray-600" />
                          View RC
                        </a>
                      ) : (
                        <FileText size={22} className="text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input ref={rcRef} type="file" accept="image/*,application/pdf" className="hidden"
                        onChange={(e) => uploadRc(e.target.files?.[0])} />
                      <Button type="button" onClick={() => rcRef.current?.click()} disabled={rcUploading}
                        className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9"
                        data-testid="upload-rc-btn">
                        {rcUploading
                          ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                          : <><Upload size={14} className="mr-2" /> Upload / Replace</>}
                      </Button>
                      <p className="text-[11px] text-gray-500 mt-1">PDF / Image · up to 10 MB.</p>
                    </div>
                  </div>
                </FieldGroup>
              </div>

              <FieldGroup label="Vehicle Name*">
                <input
                  value={editing.name}
                  onChange={(e) => handleField('name', e.target.value)}
                  placeholder="e.g. Office Innova / Front-desk scooter"
                  className="admin-input"
                  data-testid="editor-vehicle-name"
                />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FieldGroup label="Registration Number*">
                  <input
                    value={editing.registration_number}
                    onChange={(e) => handleField('registration_number', e.target.value.toUpperCase())}
                    placeholder="KA-01-AB-1234"
                    className="admin-input font-mono"
                    data-testid="editor-reg-number"
                  />
                </FieldGroup>
                <FieldGroup label="Make / Model">
                  <input
                    value={editing.make_model || ''}
                    onChange={(e) => handleField('make_model', e.target.value)}
                    placeholder="Toyota Innova Crysta"
                    className="admin-input"
                    data-testid="editor-make-model"
                  />
                </FieldGroup>
                <FieldGroup label="Fuel Type">
                  <select
                    value={editing.fuel_type || 'Petrol'}
                    onChange={(e) => handleField('fuel_type', e.target.value)}
                    className="admin-input"
                    data-testid="editor-fuel-type"
                  >
                    {FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </FieldGroup>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FieldGroup label="Type">
                  <select
                    value={editing.vehicle_type || 'Car'}
                    onChange={(e) => handleField('vehicle_type', e.target.value)}
                    className="admin-input"
                    data-testid="editor-vehicle-type"
                  >
                    {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </FieldGroup>
                <FieldGroup label="Year">
                  <input
                    type="number"
                    min="1900"
                    max="2100"
                    value={editing.year_of_purchase || ''}
                    onChange={(e) => handleField('year_of_purchase', e.target.value)}
                    placeholder="2021"
                    className="admin-input"
                    data-testid="editor-year"
                  />
                </FieldGroup>
                <FieldGroup label="Color">
                  <input
                    value={editing.color || ''}
                    onChange={(e) => handleField('color', e.target.value)}
                    placeholder="White"
                    className="admin-input"
                    data-testid="editor-color"
                  />
                </FieldGroup>
                <FieldGroup label="Status">
                  <select
                    value={editing.status || 'Active'}
                    onChange={(e) => handleField('status', e.target.value)}
                    className="admin-input"
                    data-testid="editor-status"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </FieldGroup>
              </div>

              <FieldGroup label="Assigned to (Employee)">
                <select
                  value={editing.assigned_to_employee_id || ''}
                  onChange={(e) => handleField('assigned_to_employee_id', e.target.value)}
                  className="admin-input"
                  data-testid="editor-assigned-to"
                >
                  <option value="">— Unassigned —</option>
                  {activeEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} · {emp.designation}
                    </option>
                  ))}
                </select>
              </FieldGroup>

              {/* Insurance */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <Shield size={12} /> Insurance
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FieldGroup label="Provider">
                    <input
                      value={editing.insurance_provider || ''}
                      onChange={(e) => handleField('insurance_provider', e.target.value)}
                      placeholder="ICICI Lombard"
                      className="admin-input"
                      data-testid="editor-insurance-provider"
                    />
                  </FieldGroup>
                  <FieldGroup label="Policy Number">
                    <input
                      value={editing.insurance_policy_number || ''}
                      onChange={(e) => handleField('insurance_policy_number', e.target.value)}
                      placeholder="POL-123456"
                      className="admin-input font-mono"
                      data-testid="editor-insurance-policy"
                    />
                  </FieldGroup>
                  <FieldGroup label="Expiry Date">
                    <input
                      type="date"
                      value={toISODate(editing.insurance_expiry_date)}
                      onChange={(e) => handleField('insurance_expiry_date', fromISODate(e.target.value))}
                      className="admin-input"
                      data-testid="editor-insurance-expiry"
                    />
                  </FieldGroup>
                </div>
              </div>

              {/* Maintenance */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <Wrench size={12} /> Maintenance
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <FieldGroup label="Overall Rating">
                    <div className="pt-1">
                      <StarRow
                        value={editing.maintenance_rating || 0}
                        onChange={(v) => handleField('maintenance_rating', v)}
                        size={22}
                      />
                    </div>
                  </FieldGroup>
                  <div className="md:col-span-2">
                    <FieldGroup label="Maintenance Notes">
                      <textarea
                        value={editing.maintenance_notes || ''}
                        onChange={(e) => handleField('maintenance_notes', e.target.value)}
                        placeholder="e.g. Runs smooth; brake pads to be replaced next quarter."
                        className="admin-input min-h-[68px]"
                        data-testid="editor-maintenance-notes"
                      />
                    </FieldGroup>
                  </div>
                </div>

                {/* Service history */}
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase">
                    Service History
                  </div>
                  <button
                    type="button"
                    onClick={addService}
                    className="text-xs inline-flex items-center gap-1 border border-gray-300 hover:border-gray-900 px-2 py-1"
                    data-testid="add-service-entry-btn"
                  >
                    <Plus size={12} /> Add entry
                  </button>
                </div>
                {(editing.service_history || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No service entries yet.</p>
                ) : (
                  <div className="border border-gray-200 divide-y divide-gray-200">
                    <div className="grid grid-cols-12 gap-2 px-2 py-1 bg-gray-50 text-[10px] font-mono tracking-widest text-gray-500 uppercase">
                      <div className="col-span-2">Date</div>
                      <div className="col-span-3">Service Center</div>
                      <div className="col-span-4">Description</div>
                      <div className="col-span-1 text-right">Cost</div>
                      <div className="col-span-1 text-right">KM</div>
                      <div className="col-span-1"></div>
                    </div>
                    {editing.service_history.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 px-2 py-2 items-center" data-testid={`service-row-${idx}`}>
                        <input
                          type="date"
                          value={toISODate(row.date)}
                          onChange={(e) => updateService(idx, 'date', fromISODate(e.target.value))}
                          className="col-span-2 border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-gray-900"
                        />
                        <input
                          value={row.service_center || ''}
                          onChange={(e) => updateService(idx, 'service_center', e.target.value)}
                          placeholder="Center"
                          className="col-span-3 border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-gray-900"
                        />
                        <input
                          value={row.description || ''}
                          onChange={(e) => updateService(idx, 'description', e.target.value)}
                          placeholder="Description"
                          className="col-span-4 border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-gray-900"
                        />
                        <input
                          type="number"
                          min="0"
                          value={row.cost ?? ''}
                          onChange={(e) => updateService(idx, 'cost', e.target.value)}
                          placeholder="0"
                          className="col-span-1 border border-gray-300 px-2 py-1 text-xs text-right tabular-nums focus:outline-none focus:border-gray-900"
                        />
                        <input
                          type="number"
                          min="0"
                          value={row.odometer ?? ''}
                          onChange={(e) => updateService(idx, 'odometer', e.target.value)}
                          placeholder="km"
                          className="col-span-1 border border-gray-300 px-2 py-1 text-xs text-right tabular-nums focus:outline-none focus:border-gray-900"
                        />
                        <button
                          type="button"
                          onClick={() => removeService(idx)}
                          className="col-span-1 justify-self-end p-1 text-red-500 hover:bg-red-50"
                          title="Remove"
                          data-testid={`remove-service-${idx}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    {editing.service_history.length > 0 && (
                      <div className="grid grid-cols-12 gap-2 px-2 py-2 bg-gray-50 text-[11px] font-mono">
                        <div className="col-span-9 text-right text-gray-500">Total spent</div>
                        <div className="col-span-2 text-right text-gray-900 tabular-nums font-semibold">
                          ₹ {money(editing.service_history.reduce((a, r) => a + (Number(r.cost) || 0), 0))}
                        </div>
                        <div className="col-span-1"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Purchase details */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <FileText size={12} /> Purchase Details
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FieldGroup label="Date of Purchase">
                    <input type="date" value={toISODate(editing.date_of_purchase)}
                      onChange={(e) => handleField('date_of_purchase', fromISODate(e.target.value))}
                      className="admin-input" data-testid="editor-date-of-purchase" />
                  </FieldGroup>
                  <FieldGroup label="Ownership">
                    <select value={editing.purchase_hand || ''} onChange={(e) => handleField('purchase_hand', e.target.value)}
                      className="admin-input" data-testid="editor-purchase-hand">
                      <option value="">— select —</option>
                      <option value="I Hand">I Hand (New)</option>
                      <option value="II Hand">II Hand (Used)</option>
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Purchase Invoice">
                    <div className="flex items-center gap-2">
                      <input ref={invoiceRef} type="file" accept="image/*,application/pdf" className="hidden"
                        onChange={(e) => uploadInvoice(e.target.files?.[0])} />
                      <Button type="button" onClick={() => invoiceRef.current?.click()} disabled={invoiceUploading}
                        className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9" data-testid="upload-invoice-btn">
                        {invoiceUploading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</> : <><Upload size={14} className="mr-2" /> Upload</>}
                      </Button>
                      {editing.purchase_invoice_path && (
                        <a href={`${API}/media/${editing.purchase_invoice_path}`} target="_blank" rel="noreferrer"
                          className="text-xs text-brand-blue hover:underline whitespace-nowrap" data-testid="view-invoice-link">View invoice</a>
                      )}
                    </div>
                  </FieldGroup>
                </div>
              </div>

              {/* Loan / finance */}
              <div className="border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase flex items-center gap-2">
                    <Wallet size={12} /> Loan / Finance
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="loan-taken-toggle">
                    <input type="checkbox" checked={!!editing.loan_taken}
                      onChange={(e) => handleField('loan_taken', e.target.checked)} className="accent-gray-900" />
                    Loan taken?
                  </label>
                </div>

                {editing.loan_taken && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FieldGroup label="Bank / Lender">
                        <input value={editing.loan_bank || ''} onChange={(e) => handleField('loan_bank', e.target.value)}
                          placeholder="e.g. HDFC Bank" className="admin-input" data-testid="editor-loan-bank" />
                      </FieldGroup>
                      <FieldGroup label="Loan Amount (₹)">
                        <input type="number" min="0" value={editing.loan_amount} onChange={(e) => handleField('loan_amount', e.target.value)}
                          className="admin-input tabular-nums" data-testid="editor-loan-amount" />
                      </FieldGroup>
                      <FieldGroup label="Interest Rate (% p.a.)">
                        <input type="number" min="0" step="0.01" value={editing.interest_rate} onChange={(e) => handleField('interest_rate', e.target.value)}
                          className="admin-input tabular-nums" data-testid="editor-interest-rate" />
                      </FieldGroup>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <FieldGroup label="Sanction Date">
                        <input type="date" value={toISODate(editing.loan_sanction_date)}
                          onChange={(e) => handleField('loan_sanction_date', fromISODate(e.target.value))} className="admin-input" data-testid="editor-sanction-date" />
                      </FieldGroup>
                      <FieldGroup label="EMI Date">
                        <input value={editing.emi_date || ''} onChange={(e) => handleField('emi_date', e.target.value)}
                          placeholder="e.g. 5th of month" className="admin-input" data-testid="editor-emi-date" />
                      </FieldGroup>
                      <FieldGroup label="EMI Amount (₹)">
                        <input type="number" min="0" value={editing.emi_amount} onChange={(e) => handleField('emi_amount', e.target.value)}
                          className="admin-input tabular-nums" data-testid="editor-emi-amount" />
                      </FieldGroup>
                      <FieldGroup label="Loan Closure Date">
                        <input type="date" value={toISODate(editing.loan_closure_date)}
                          onChange={(e) => handleField('loan_closure_date', fromISODate(e.target.value))} className="admin-input" data-testid="editor-loan-closure" />
                      </FieldGroup>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FieldGroup label="Lien Status">
                        <select value={editing.lien_status || ''} onChange={(e) => handleField('lien_status', e.target.value)}
                          className="admin-input" data-testid="editor-lien-status">
                          <option value="">— select —</option>
                          <option value="Active">Active (hypothecated)</option>
                          <option value="Cleared">Cleared</option>
                          <option value="Marked to RTO">Marked to RTO</option>
                          <option value="NOC Received">NOC Received</option>
                        </select>
                      </FieldGroup>
                      <FieldGroup label="EMI Schedule">
                        <div className="flex items-center gap-2">
                          <input ref={emiRef} type="file" accept="image/*,application/pdf,.csv,.xlsx,.xls,.xlsm" className="hidden"
                            onChange={(e) => uploadEmiSchedule(e.target.files?.[0])} />
                          <Button type="button" onClick={() => emiRef.current?.click()} disabled={emiUploading}
                            className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9" data-testid="upload-emi-btn">
                            {emiUploading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Reading…</> : <><Upload size={14} className="mr-2" /> Upload</>}
                          </Button>
                          {editing.emi_schedule_path && (
                            <a href={`${API}/media/${editing.emi_schedule_path}`} target="_blank" rel="noreferrer"
                              className="text-xs text-brand-blue hover:underline whitespace-nowrap" data-testid="view-emi-link">View</a>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1">CSV/XLSX with an &ldquo;interest&rdquo; column auto-fills total interest.</p>
                      </FieldGroup>
                      <FieldGroup label="Total Interest on Loan (₹)">
                        <input type="number" min="0" value={editing.total_loan_interest} onChange={(e) => handleField('total_loan_interest', e.target.value)}
                          className="admin-input tabular-nums" data-testid="editor-total-loan-interest" />
                      </FieldGroup>
                    </div>

                    {/* Cost of ownership: service + loan interest */}
                    {(() => {
                      const svc = (editing.service_history || []).reduce((a, r) => a + (Number(r.cost) || 0), 0);
                      const interest = Number(editing.total_loan_interest) || 0;
                      return (
                        <div className="bg-gray-50 border border-gray-200 px-3 py-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs" data-testid="cost-of-ownership">
                          <span className="font-mono text-[10px] tracking-widest uppercase text-gray-500">Cost of ownership</span>
                          <span className="text-gray-600">Service <b className="tabular-nums text-gray-900">₹{money(svc)}</b></span>
                          <span className="text-gray-600">Loan interest <b className="tabular-nums text-gray-900">₹{money(interest)}</b></span>
                          <span className="ml-auto text-gray-900 font-heading">Total <b className="tabular-nums">₹{money(svc + interest)}</b></span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Sort Order">
                  <input
                    type="number"
                    value={editing.sort_order ?? 100}
                    onChange={(e) => handleField('sort_order', e.target.value)}
                    className="admin-input"
                    data-testid="editor-sort-order"
                  />
                </FieldGroup>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo lightbox — 4in × 5in (approx 384px × 480px at 96 DPI). */}
      {zoomPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
          onClick={() => setZoomPhoto(null)}
          data-testid="vehicle-photo-lightbox"
        >
          <div
            className="bg-white p-3 shadow-2xl"
            style={{ width: '4in', maxWidth: '96vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-gray-100" style={{ width: '100%', height: '5in', maxHeight: '80vh' }}>
              <img
                src={zoomPhoto.url}
                alt={zoomPhoto.name}
                className="w-full h-full object-contain"
              />
              <button
                onClick={() => setZoomPhoto(null)}
                className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white p-1 shadow"
                title="Close"
                data-testid="close-photo-lightbox"
              >
                <X size={14} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="text-xs text-gray-700 truncate">{zoomPhoto.name}</div>
              <a
                href={zoomPhoto.url}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-mono uppercase tracking-widest text-brand-blue hover:underline whitespace-nowrap"
              >
                Open full size
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 10px 12px;
          font-size: 14px;
          outline: none;
          background: white;
        }
        .admin-input:focus { border-color: #111827; }
      `}</style>
    </div>
  );
};

const KPI = ({ label, value, sub, icon, tone }) => {
  const toneMap = {
    yellow: 'bg-[#0a0f1e] text-brand-yellow',
    green:  'bg-[#0a0f1e] text-green-400',
    amber:  'bg-[#0a0f1e] text-amber-400',
    red:    'bg-[#0a0f1e] text-red-400',
    mono:   'bg-[#0a0f1e] text-white',
  };
  return (
    <div className={`${toneMap[tone] || toneMap.mono} p-5`}>
      <div className="flex items-center gap-2 mb-2 text-white/60">
        {icon}
        <span className="font-mono text-[10px] tracking-widest uppercase">{label}</span>
      </div>
      <div className="font-heading text-2xl md:text-3xl font-black tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-white/50 mt-1">{sub}</div>}
    </div>
  );
};

export default VehicleDiaryPage;
